// src/components/AuthGuard.jsx
import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";

// Where a fitter who lands on the admin panel gets sent.
const FITTER_PORTAL_URL = "https://fitter.princessflooring.com";

function Spinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
    </div>
  );
}

/**
 * A fitter account has no place in the admin panel. Sign it out of the shared
 * Firebase project, then hand off to the Fitter Portal (cross-domain, so a hard
 * navigation rather than a react-router redirect).
 */
function BounceToFitterPortal() {
  useEffect(() => {
    signOut(auth).finally(() => {
      window.location.replace(FITTER_PORTAL_URL);
    });
  }, []);
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <p className="text-gray-600">
        This is a fitter account — taking you to the Fitter Portal…
      </p>
    </div>
  );
}

function AuthGuard({ children, requireAuth = true }) {
  const { user, isFitter, loading } = useAuth();

  if (loading) return <Spinner />;

  // Any signed-in fitter — on a protected route OR the login page — is bounced
  // to the Fitter Portal. This panel is admin-only.
  if (user && isFitter) return <BounceToFitterPortal />;

  // Not signed in on a protected route → login.
  if (requireAuth && !user) return <Navigate to="/login" replace />;

  // Admin already signed in and sitting on /login → into the app.
  if (!requireAuth && user) return <Navigate to="/customers" replace />;

  return children;
}

export default AuthGuard;
